// src/lib/stock-shapes.ts
import type { ReactNode } from 'react'
import { renderShapeSvg } from './stock-shape-render'

export type ShapeId =
  | 'rectangular'
  | 'round_bar'
  | 'hex_bar'
  | 'plate'
  | 'tube'
  | 'l_angle'
  | 't_section'
  | 'irregular_drawing'

export type ShapeCategory = 'block' | 'bar' | 'structural' | 'irregular'

export interface DimensionField {
  key: string
  label: string         // SVG callout symbol: 'Ø', 'L', 'A', etc.
  placeholder: string   // 'Diameter', 'Length', ...
  min?: number
}

export type SvgVariant = 'glyph' | 'card' | 'full'

export interface StockShapeDef {
  id: ShapeId
  label: string
  description: string
  category: ShapeCategory
  dimensions: DimensionField[]
  renderSvg(variant: SvgVariant, values?: Record<string, number>): ReactNode
  toBoundingBox(values: Record<string, number>): { x: number; y: number; z: number }
  formatSummary(values: Record<string, number>, units: 'mm' | 'in'): string
}

// Helper for the irregular drawing case
function passthroughBoundingBox(v: Record<string, number>) {
  return { x: v.x ?? 0, y: v.y ?? 0, z: v.z ?? 0 }
}

const defs: Record<ShapeId, Omit<StockShapeDef, 'renderSvg'>> = {
  rectangular: {
    id: 'rectangular',
    label: 'Rectangular block',
    description: 'Standard milling stock. Best for general 2.5D work.',
    category: 'block',
    dimensions: [
      { key: 'x', label: 'X', placeholder: 'Width',  min: 0 },
      { key: 'y', label: 'Y', placeholder: 'Depth',  min: 0 },
      { key: 'z', label: 'Z', placeholder: 'Height', min: 0 },
    ],
    toBoundingBox: (v) => ({ x: v.x ?? 0, y: v.y ?? 0, z: v.z ?? 0 }),
    formatSummary: (v, u) => `${v.x ?? 0} × ${v.y ?? 0} × ${v.z ?? 0} ${u}`,
  },
  round_bar: {
    id: 'round_bar',
    label: 'Round bar',
    description: 'Cylindrical stock. Best for turning or round-profile parts.',
    category: 'bar',
    dimensions: [
      { key: 'diameter', label: 'Ø', placeholder: 'Diameter', min: 0 },
      { key: 'length',   label: 'L', placeholder: 'Length',   min: 0 },
    ],
    toBoundingBox: (v) => ({ x: v.diameter ?? 0, y: v.diameter ?? 0, z: v.length ?? 0 }),
    formatSummary: (v, u) => `Ø${v.diameter ?? 0} × L${v.length ?? 0} ${u}`,
  },
  hex_bar: {
    id: 'hex_bar',
    label: 'Hex bar',
    description: 'Six-sided bar stock. Common for fittings and fasteners.',
    category: 'bar',
    dimensions: [
      { key: 'af',     label: 'AF', placeholder: 'Across flats', min: 0 },
      { key: 'length', label: 'L',  placeholder: 'Length',       min: 0 },
    ],
    // Hex bounding box: across-corners = AF / cos(30°) ≈ AF * 1.1547
    toBoundingBox: (v) => {
      const af = v.af ?? 0
      const ac = af / Math.cos(Math.PI / 6)
      return { x: ac, y: af, z: v.length ?? 0 }
    },
    formatSummary: (v, u) => `${v.af ?? 0}AF × L${v.length ?? 0} ${u} (hex)`,
  },
  plate: {
    id: 'plate',
    label: 'Plate / sheet',
    description: 'Thin flat stock. Best for sheet-metal-like 2.5D work.',
    category: 'block',
    dimensions: [
      { key: 'x',         label: 'X', placeholder: 'Width',     min: 0 },
      { key: 'y',         label: 'Y', placeholder: 'Length',    min: 0 },
      { key: 'thickness', label: 't', placeholder: 'Thickness', min: 0 },
    ],
    toBoundingBox: (v) => ({ x: v.x ?? 0, y: v.y ?? 0, z: v.thickness ?? 0 }),
    formatSummary: (v, u) => `${v.x ?? 0} × ${v.y ?? 0} × ${v.thickness ?? 0}${u} (plate)`,
  },
  tube: {
    id: 'tube',
    label: 'Tube / pipe',
    description: 'Hollow round stock. OD outside, ID inside.',
    category: 'bar',
    dimensions: [
      { key: 'od',     label: 'OD', placeholder: 'Outer diameter', min: 0 },
      { key: 'id',     label: 'ID', placeholder: 'Inner diameter', min: 0 },
      { key: 'length', label: 'L',  placeholder: 'Length',         min: 0 },
    ],
    toBoundingBox: (v) => ({ x: v.od ?? 0, y: v.od ?? 0, z: v.length ?? 0 }),
    formatSummary: (v, u) =>
      `OD${v.od ?? 0}/ID${v.id ?? 0} × L${v.length ?? 0} ${u} (tube)`,
  },
  l_angle: {
    id: 'l_angle',
    label: 'L-angle',
    description: 'L-shaped extrusion. Two legs joined at 90°.',
    category: 'structural',
    dimensions: [
      { key: 'legA',      label: 'A', placeholder: 'Leg A',     min: 0 },
      { key: 'legB',      label: 'B', placeholder: 'Leg B',     min: 0 },
      { key: 'thickness', label: 't', placeholder: 'Thickness', min: 0 },
      { key: 'length',    label: 'L', placeholder: 'Length',    min: 0 },
    ],
    toBoundingBox: (v) => ({ x: v.legA ?? 0, y: v.legB ?? 0, z: v.length ?? 0 }),
    formatSummary: (v, u) =>
      `${v.legA ?? 0}×${v.legB ?? 0}×${v.thickness ?? 0} × L${v.length ?? 0} ${u} (L)`,
  },
  t_section: {
    id: 't_section',
    label: 'T-section',
    description: 'T-shaped structural extrusion. Flange on top, web below.',
    category: 'structural',
    dimensions: [
      { key: 'flangeW', label: 'fW', placeholder: 'Flange width',     min: 0 },
      { key: 'flangeT', label: 'ft', placeholder: 'Flange thickness', min: 0 },
      { key: 'webH',    label: 'wH', placeholder: 'Web height',       min: 0 },
      { key: 'webT',    label: 'wt', placeholder: 'Web thickness',    min: 0 },
      { key: 'length',  label: 'L',  placeholder: 'Length',           min: 0 },
    ],
    toBoundingBox: (v) => ({
      x: v.flangeW ?? 0,
      y: (v.flangeT ?? 0) + (v.webH ?? 0),
      z: v.length ?? 0,
    }),
    formatSummary: (v, u) =>
      `T ${v.flangeW ?? 0}×${v.webH ?? 0} × L${v.length ?? 0} ${u}`,
  },
  irregular_drawing: {
    id: 'irregular_drawing',
    label: 'Pre-machined / drawing',
    description: 'Irregular stock. Upload a drawing (DXF/DWG/PDF/STEP) of your stock and align the part inside it.',
    category: 'irregular',
    dimensions: [
      { key: 'x', label: 'X', placeholder: '(from drawing)', min: 0 },
      { key: 'y', label: 'Y', placeholder: '(from drawing)', min: 0 },
      { key: 'z', label: 'Z', placeholder: '(from drawing)', min: 0 },
    ],
    toBoundingBox: passthroughBoundingBox,
    formatSummary: (v, u) =>
      `${v.x ?? 0} × ${v.y ?? 0} × ${v.z ?? 0} ${u} (drawing)`,
  },
}

export const STOCK_SHAPES: Record<ShapeId, StockShapeDef> = {} as Record<ShapeId, StockShapeDef>

;(Object.keys(defs) as ShapeId[]).forEach((id) => {
  STOCK_SHAPES[id] = { ...defs[id], renderSvg: (v, vals) => renderShapeSvg(id, v, vals) }
})

export const SHAPE_ORDER: ShapeId[] = [
  'rectangular', 'round_bar', 'hex_bar', 'plate',
  'tube', 'l_angle', 't_section', 'irregular_drawing',
]

import type { StockSize } from '../types'

export function makeRectangularStock(x: number, y: number, z: number): StockSize {
  return {
    shapeId: 'rectangular',
    dimensions: { x, y, z },
    x, y, z,
  }
}

export function recomputeBoundingBox(stock: StockSize): StockSize {
  const bbox = STOCK_SHAPES[stock.shapeId].toBoundingBox(stock.dimensions)
  return { ...stock, ...bbox }
}
